import React from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';

// TriageResultCard now renders ONLY real data coming from props or backend/AI.
// No hard-coded placeholders: fields render only if provided.
// It supports two usage patterns:
// 1) Pass a fully-formed `result` object with fields below.
// 2) Pass individual props used by AssetTriagePage today (hasTriageResult, isProcessing, strategyKey, simulateGeminiTriage).

const TriageResultCard = ({
  // Preferred: structured result from backend/AI or computed on page
  result = {},
  // Selection/apply
  isSelected = false,
  onApply = null,
  // Compatibility with current AssetTriagePage usage
  hasTriageResult = false,
  isProcessing = false,
  strategyKey = null,
  simulateGeminiTriage = null,
}) => {
  // Normalize fields — do NOT inject fake defaults
  const strategy = result.strategy || strategyKey || null;
  const confidence = Number.isFinite(result.confidence) ? result.confidence : null;
  const impact = Number.isFinite(result.impact) ? result.impact : null; // expected 0–5 scale (optional)
  const cost = result.cost ?? null; // string/value provided by backend/AI (optional)
  const time = result.time ?? null; // string/value provided by backend/AI (optional)
  const description = result.description || null;
  const risk = result.risk || null;

  const riskColor = risk
    ? ({ High: 'text-red-500', Medium: 'text-yellow-500', Low: 'text-green-500' }[risk] || 'text-gray-500')
    : null;

  const showHeader = Boolean(strategy || description || risk || confidence != null || isProcessing);
  const canApply = Boolean(onApply && strategy);

  // If nothing real to show yet, display a neutral call-to-action (no fake values)
  if (!showHeader && !hasTriageResult) {
    return (
      <div className="border rounded-lg p-4 mb-4 border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">Strategy Recommendation</h3>
            <p className="text-sm text-gray-600 mt-1">Run triage to generate a data-driven recommendation.</p>
          </div>
          {typeof simulateGeminiTriage === 'function' && (
            <button
              onClick={simulateGeminiTriage}
              className="flex items-center text-sm font-medium px-3 py-1.5 rounded-md text-cyan-700 bg-cyan-50 hover:bg-cyan-100"
            >
              <span>Run Triage</span>
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border rounded-lg p-4 mb-4 transition-all ${
        isSelected ? 'border-2 border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:border-cyan-300 hover:shadow-md'
      }`}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center">
            <h3 className="font-semibold text-lg text-gray-800">
              {strategy || '—'}
            </h3>
            {risk && riskColor && (
              <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${riskColor} bg-opacity-10 ${riskColor.replace('text-', 'bg-')}`}>
                {risk} Risk
              </span>
            )}
          </div>
          {description && (
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          )}
        </div>
        <div className="flex items-center ml-2">
          {isProcessing ? (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Processing…</span>
          ) : confidence != null ? (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              isSelected ? 'bg-cyan-100 text-cyan-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {Math.round(confidence)}% confidence
            </span>
          ) : null}
        </div>
      </div>

      {(impact != null || cost != null || time != null) && (
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          {impact != null && (
            <div>
              <p className="text-gray-500 text-xs font-medium">Impact</p>
              <div className="flex items-center mt-1">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full mr-1 ${i < impact ? 'bg-cyan-500' : 'bg-gray-200'}`}
                  />
                ))}
              </div>
            </div>
          )}
          {cost != null && (
            <div>
              <p className="text-gray-500 text-xs font-medium">Cost</p>
              <p className="font-medium text-gray-800">{String(cost)}</p>
            </div>
          )}
          {time != null && (
            <div>
              <p className="text-gray-500 text-xs font-medium">Time</p>
              <p className="font-medium text-gray-800">{String(time)}</p>
            </div>
          )}
        </div>
      )}

      {canApply && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => onApply({ strategy, confidence, impact, cost, time, description, risk })}
            className={`flex items-center text-sm font-medium px-3 py-1.5 rounded-md ${
              isSelected ? 'bg-cyan-600 text-white hover:bg-cyan-700' : 'text-cyan-600 hover:bg-cyan-50'
            } transition-colors`}
          >
            {isSelected ? (
              <>
                <CheckCircle className="w-4 h-4 mr-1.5" />
                <span>Selected</span>
              </>
            ) : (
              <>
                <span>Select Strategy</span>
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default TriageResultCard;
